import React, { useState, useEffect, useCallback, memo, useRef, useMemo } from 'react';
import { studiesAPI } from '../../services/api';
import { buildAllowedKeywordsSet } from '../../utils/similarityPairs';
import { formatNumber } from '../../utils/formatters';
import { exportToCSV, exportToExcel, CLUSTER_EXPORT_COLUMNS } from '../../utils/exportUtils';
import Icon from '../common/Icon';
import './OptimizationView.css';

const cleanUrl = (url) => {
  if (!url) return '-';
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    params.delete('srsltid');
    
    const cleanedSearch = params.toString();
    let cleanedUrl = `${urlObj.protocol}//${urlObj.host}${decodeURIComponent(urlObj.pathname)}`;
    
    if (cleanedSearch) {
      cleanedUrl += `?${cleanedSearch}`;
    }
    return cleanedUrl;
  } catch (error) {
    return url;
  }
};

const TargetUrlInput = memo(({ clusterId, initialValue, onValueChange }) => {
  const [localValue, setLocalValue] = useState(initialValue || '');

  useEffect(() => {
    setLocalValue(initialValue || '');
  }, [initialValue]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onValueChange(clusterId, newValue);
  };

  return (
    <input
      id={`target-url-${clusterId}`}
      type="text"
      value={localValue}
      onChange={handleChange}
      placeholder="https://example.com/page-optimisee"
      className="target-url-input"
    />
  );
});

TargetUrlInput.displayName = 'TargetUrlInput';

const ClusterCard = memo(({ cluster, isViewed, onToggleViewed, onUseUrl, onTargetUrlChange }) => {
  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    onToggleViewed(cluster.id);
  }, [cluster.id, onToggleViewed]);

  return (
    <div className={`cluster-optimization-card ${isViewed ? 'viewed' : ''}`}>
      <div className="cluster-optimization-header">
        <div className="cluster-title">
          <h3>{cluster.mainKeyword}</h3>
          <span className="cluster-badge">
            {cluster.keywordCount} mot{cluster.keywordCount > 1 ? 's' : ''}-clé{cluster.keywordCount > 1 ? 's' : ''}
          </span>
        </div>
        <div className="cluster-header-right">
          <div className="cluster-metrics">
            <span className="metric">
              <Icon name="TrendingUp" size={14} />
              {formatNumber(cluster.totalVolume)} vol.
            </span>
            <span className="metric">
              <Icon name="Users" size={14} />
              {formatNumber(cluster.totalTraffic)} trafic
            </span>
            {cluster.keywordCount > 1 && (
              <span className="metric">
                <Icon name="Percent" size={14} />
                {cluster.averageSimilarity}% similarité
              </span>
            )}
          </div>
          <button
            className={`btn-viewed ${isViewed ? 'active' : ''}`}
            onClick={handleToggle}
            title={isViewed ? "Marquer comme non consulté" : "Marquer comme consulté"}
          >
            <Icon name="Check" size={16} />
          </button>
        </div>
      </div>

      <div className={`cluster-content ${isViewed ? 'hidden' : ''}`}>
          <div className="target-url-section">
            <label htmlFor={`target-url-${cluster.id}`}>
              <Icon name="Target" size={16} />
              URL à Positionner:
            </label>
            <TargetUrlInput
              clusterId={cluster.id}
              initialValue={cluster.targetUrl}
              onValueChange={onTargetUrlChange}
            />
            {cluster.targetUrl && (
              <small className="url-suggestion-hint">
                💡 URL suggérée basée sur les meilleures positions et volumes du cluster
              </small>
            )}
          </div>

          <div className="keywords-table-wrapper">
            <table className="keywords-optimization-table">
              <thead>
                <tr>
                  <th>Mot-clé</th>
                  <th>Volume</th>
                  <th>Similarité</th>
                  <th>Position</th>
                  <th>Trafic estimé</th>
                  <th>URL actuelle</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr className="main-keyword-row">
                  <td>
                    <strong>{cluster.mainKeyword}</strong>
                    <span className="main-badge">Principal</span>
                  </td>
                  <td>{formatNumber(cluster.mainVolume)}</td>
                  <td>100%</td>
                  <td>
                    {cluster.mainPosition ? (
                      <span className={`position-badge position-${cluster.mainPosition <= 3 ? 'top' : cluster.mainPosition <= 10 ? 'good' : 'low'}`}>
                        #{cluster.mainPosition}
                      </span>
                    ) : (
                      <span className="no-position">-</span>
                    )}
                  </td>
                  <td>{formatNumber(cluster.mainTraffic)}</td>
                  <td className="url-cell" title={cluster.mainUrl}>
                    {cluster.mainUrl ? (
                      <a href={cluster.mainUrl} target="_blank" rel="noopener noreferrer">
                        {(() => {
                          const cleaned = cleanUrl(cluster.mainUrl);
                          return cleaned.length > 50 ? cleaned.substring(0, 50) + '...' : cleaned;
                        })()}
                      </a>
                    ) : (
                      <span className="no-url">-</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    {cluster.mainUrl && (
                      <button
                        className="btn-use-url"
                        onClick={() => onUseUrl(cluster.id, cluster.mainUrl)}
                        title="Utiliser cette URL"
                      >
                        <Icon name="ArrowRight" size={14} />
                      </button>
                    )}
                  </td>
                </tr>
                {(cluster.keywords || []).map((kw, idx) => (
                  <tr key={`${cluster.id}-${idx}`}>
                    <td>{kw.keyword}</td>
                    <td>{formatNumber(kw.volume)}</td>
                    <td>{kw.similarityScore}%</td>
                    <td>
                      {kw.position ? (
                        <span className={`position-badge position-${kw.position <= 3 ? 'top' : kw.position <= 10 ? 'good' : 'low'}`}>
                          #{kw.position}
                        </span>
                      ) : (
                        <span className="no-position">-</span>
                      )}
                    </td>
                    <td>{formatNumber(kw.estimatedTraffic)}</td>
                    <td className="url-cell" title={kw.url}>
                      {kw.url ? (
                        <a href={kw.url} target="_blank" rel="noopener noreferrer">
                          {(() => {
                            const cleaned = cleanUrl(kw.url);
                            return cleaned.length > 50 ? cleaned.substring(0, 50) + '...' : cleaned;
                          })()}
                        </a>
                      ) : (
                        <span className="no-url">-</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      {kw.url && (
                        <button
                          className="btn-use-url"
                          onClick={() => onUseUrl(cluster.id, kw.url)}
                          title="Utiliser cette URL"
                        >
                          <Icon name="ArrowRight" size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.cluster.id === nextProps.cluster.id &&
    prevProps.isViewed === nextProps.isViewed &&
    prevProps.cluster.targetUrl === nextProps.cluster.targetUrl
  );
});

ClusterCard.displayName = 'ClusterCard';

const CLUSTERS_PER_PAGE = 20;

const OptimizationView = ({ studyId, keywords = [], activeFilters: propsActiveFilters = [], regexFilters: propsRegexFilters = { regexInclude: '', regexExclude: '' } }) => {
  const [repeatClusterName, setRepeatClusterName] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [excludedKeywords, setExcludedKeywords] = useState([]);
  const [savedFilters, setSavedFilters] = useState({ activeFilters: [], regexFilters: { regexInclude: '', regexExclude: '' } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewedClusters, setViewedClusters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const urlMapRef = useRef(new Map());
  const exportDropdownRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef(false);

  const debouncedSave = useCallback((clustersToSave) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    pendingChangesRef.current = true;
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await studiesAPI.saveClusters(studyId, { clusters: clustersToSave, orphans: [] });
        pendingChangesRef.current = false;
      } catch (err) {
        console.error('Failed to save clusters:', err);
      }
    }, 1000);
  }, [studyId]);

  const handleTargetUrlChange = useCallback((clusterId, newUrl) => {
    urlMapRef.current.set(clusterId, newUrl);
    setClusters(prev => {
      const updated = prev.map(cluster =>
        cluster.id === clusterId ? { ...cluster, targetUrl: newUrl } : cluster
      );
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const calculatePositionWeight = (position) => {
    if (!position) return 1;
    if (position <= 10) return 3;
    if (position <= 20) return 2;
    return 1;
  };

  const addUrlScore = useCallback((urlScores, url, position, volume, multiplier = 1) => {
    const cleanedUrl = cleanUrl(url);
    const positionWeight = calculatePositionWeight(position);
    const volumeWeight = Math.log10(volume || 1);
    urlScores[cleanedUrl] = (urlScores[cleanedUrl] || 0) + (positionWeight * volumeWeight * multiplier);
  }, []);

  const findBestUrl = useCallback((urlScores) => {
    if (Object.keys(urlScores).length === 0) return null;
    const [bestUrl] = Object.entries(urlScores).sort(([, a], [, b]) => b - a)[0];
    return bestUrl;
  }, []);

  const suggestTargetUrl = useCallback((cluster) => {
    if (!cluster || cluster.keywordCount === 0) return null;

    const urlScores = {};
    
    if (cluster.mainUrl) {
      addUrlScore(urlScores, cluster.mainUrl, cluster.mainPosition, cluster.mainVolume, 2);
    }

    (cluster.keywords || []).forEach(kw => {
      if (kw.url) {
        addUrlScore(urlScores, kw.url, kw.position, kw.volume);
      }
    });

    return findBestUrl(urlScores);
  }, [addUrlScore, findBestUrl]);

  const enrichClustersWithSuggestions = useCallback((clusters) => {
    return clusters.map((cluster, index) => ({
      ...cluster,
      id: cluster.id || `cluster-${cluster.mainKeyword || index}`,
      targetUrl: cluster.targetUrl || suggestTargetUrl(cluster) || ''
    }));
  }, [suggestTargetUrl]);

  const loadClusters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await studiesAPI.getClusters(studyId);
      
      const allClusters = data.clustersData.clusters || [];
      const excluded = data.clustersData.excludedKeywords || [];
      const filters = data.clustersData.savedFilters || { activeFilters: [], regexFilters: { regexInclude: '', regexExclude: '' } };
      
      const clustersWithSuggestions = enrichClustersWithSuggestions(allClusters);
      
      setClusters(clustersWithSuggestions);
      setExcludedKeywords(excluded);
      setSavedFilters(filters);
      setCurrentPage(1);
    } catch (err) {
      if (err.message.includes('No clusters found')) {
        setClusters([]);
        setError('Aucune clusterisation sauvegardée. Veuillez d\'abord créer des clusters dans l\'onglet Clusterisation.');
      } else {
        setError(`Erreur de chargement: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [studyId, enrichClustersWithSuggestions]);

  useEffect(() => {
    loadClusters();
  }, [loadClusters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    if (exportDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportDropdownOpen]);

  const handleUseUrl = useCallback((clusterId, url) => {
    urlMapRef.current.set(clusterId, url);
    setClusters(prev =>
      prev.map(cluster =>
        cluster.id === clusterId ? { ...cluster, targetUrl: url } : cluster
      )
    );
  }, []);

  const toggleViewed = useCallback((clusterId) => {
    if (!clusterId) return;
    setViewedClusters(prev => ({
      ...prev,
      [clusterId]: !prev[clusterId]
    }));
  }, []);

  const getTargetUrl = useCallback((cluster) => {
    return urlMapRef.current.get(cluster.id) ?? cluster.targetUrl ?? '';
  }, []);

  const createMainKeywordExportRow = (cluster) => ({
    'Cluster': cluster.mainKeyword,
    'Mot-clé': cluster.mainKeyword,
    'Volume': cluster.mainVolume || 0,
    'Similarité': '100%',
    'Position': cluster.mainPosition || '-',
    'Trafic estimé': cluster.mainTraffic || 0,
    'URL actuelle': cluster.mainUrl || '-',
    'URL à positionner': getTargetUrl(cluster)
  });

  const createSecondaryKeywordExportRow = (cluster, keyword, repeatCluster = false) => ({
    'Cluster': repeatCluster ? cluster.mainKeyword : '',
    'Mot-clé': keyword.keyword,
    'Volume': keyword.volume || 0,
    'Similarité': `${keyword.similarityScore || 0}%`,
    'Position': keyword.position || '-',
    'Trafic estimé': keyword.estimatedTraffic || 0,
    'URL actuelle': keyword.url || '-',
    'URL à positionner': getTargetUrl(cluster)
  });

  const applyOperatorFilter = useCallback((value, operator, filterVal, filterVal2) => {
    if (filterVal === '' || filterVal === undefined) return true;
    const numVal = parseFloat(filterVal);
    const numVal2 = filterVal2 ? parseFloat(filterVal2) : null;
    if (isNaN(numVal)) return true;
    
    switch (operator) {
      case '>': return value > numVal;
      case '<': return value < numVal;
      case '=': return value === numVal;
      case '>=': return value >= numVal;
      case '<=': return value <= numVal;
      case 'between': return numVal2 !== null && !isNaN(numVal2) ? value >= numVal && value <= numVal2 : true;
      default: return true;
    }
  }, []);

  const activeFilters = useMemo(() => {
    return propsActiveFilters.length > 0 ? propsActiveFilters : savedFilters.activeFilters;
  }, [propsActiveFilters, savedFilters.activeFilters]);

  const regexFilters = useMemo(() => {
    const hasPropsRegex = propsRegexFilters.regexInclude?.trim() || propsRegexFilters.regexExclude?.trim();
    return hasPropsRegex ? propsRegexFilters : savedFilters.regexFilters;
  }, [propsRegexFilters, savedFilters.regexFilters]);

  const filterTypeConfig = useMemo(() => ({
    cluster: {
      volume: { label: 'Volume total', getValue: (c) => c.totalVolume || 0 },
      keywordCount: { label: 'Nb mots-clés', getValue: (c) => (c.keywords?.length || 0) + 1 },
      position: { label: 'Position', getValue: (c) => c.mainPosition },
      traffic: { label: 'Trafic', getValue: (c) => c.mainTraffic || 0 }
    },
    keyword: {
      volume: { label: 'Volume', getValue: (kw) => kw.volume || 0 },
      position: { label: 'Position', getValue: (kw) => kw.position },
      traffic: { label: 'Trafic', getValue: (kw) => kw.estimatedTraffic || 0 }
    }
  }), []);

  const applyFiltersToCluster = useCallback((cluster) => {
    const clusterTypeFilters = activeFilters.filter(f => f.category === 'cluster' && f.value !== '');
    const keywordTypeFilters = activeFilters.filter(f => f.category === 'keyword' && f.value !== '');
    
    for (const filter of clusterTypeFilters) {
      const config = filterTypeConfig.cluster[filter.field];
      if (config) {
        const val = config.getValue(cluster);
        if (filter.field === 'position' && !val) return null;
        if (!applyOperatorFilter(val, filter.operator, filter.value, filter.value2)) return null;
      }
    }
    
    if (regexFilters.regexInclude?.trim()) {
      try {
        const regex = new RegExp(regexFilters.regexInclude, 'i');
        if (!regex.test(cluster.mainKeyword) && !cluster.keywords?.some(kw => regex.test(kw.keyword))) {
          return null;
        }
      } catch (e) {}
    }
    
    if (regexFilters.regexExclude?.trim()) {
      try {
        const regex = new RegExp(regexFilters.regexExclude, 'i');
        if (regex.test(cluster.mainKeyword) || cluster.keywords?.some(kw => regex.test(kw.keyword))) {
          return null;
        }
      } catch (e) {}
    }
    
    if (keywordTypeFilters.length > 0) {
      const matchesFilter = (kw) => {
        for (const filter of keywordTypeFilters) {
          const config = filterTypeConfig.keyword[filter.field];
          if (config) {
            const val = config.getValue(kw);
            if (filter.field === 'position' && !val) return false;
            if (!applyOperatorFilter(val, filter.operator, filter.value, filter.value2)) return false;
          }
        }
        return true;
      };
      
      const mainMatches = matchesFilter({ 
        volume: cluster.mainVolume, 
        position: cluster.mainPosition, 
        estimatedTraffic: cluster.mainTraffic 
      });
      
      const filteredKeywords = cluster.keywords?.filter(kw => matchesFilter(kw)) || [];
      
      if (!mainMatches && filteredKeywords.length === 0) return null;
      
      return {
        ...cluster,
        keywords: filteredKeywords
      };
    }
    
    return cluster;
  }, [activeFilters, regexFilters, applyOperatorFilter, filterTypeConfig]);

  const allowedKeywordsSet = useMemo(() => buildAllowedKeywordsSet(keywords), [keywords]);

  const categoryFilteredClusters = useMemo(() => {
    let result = clusters
      .map(c => applyFiltersToCluster(c))
      .filter(Boolean);

    if (allowedKeywordsSet.size > 0) {
      result = result.filter(cluster => {
        const secondaryKeywords = (cluster.keywords || []).map(kw => kw.keyword);
        const clusterKeywords = [cluster.mainKeyword, ...secondaryKeywords];
        return clusterKeywords.some(kw => allowedKeywordsSet.has(kw?.toLowerCase()));
      });
    }

    return result;
  }, [clusters, applyFiltersToCluster, allowedKeywordsSet]);

  const filteredClusters = useMemo(() => {
    if (!searchTerm.trim()) return categoryFilteredClusters;

    const term = searchTerm.toLowerCase();
    return categoryFilteredClusters.filter(c =>
      c.mainKeyword?.toLowerCase().includes(term) ||
      c.keywords?.some(kw => kw.keyword?.toLowerCase().includes(term))
    );
  }, [categoryFilteredClusters, searchTerm]);

  const handleExport = (includeZeroVolume = true, separateExcluded = false, repeatCluster = false) => {
    setExportDropdownOpen(false);
    const dateStr = new Date().toISOString().split('T')[0];
    const hasFilters = activeFilters.length > 0 || regexFilters.regexInclude?.trim() || regexFilters.regexExclude?.trim();
    const suffix = hasFilters ? `_filtre` : '';
    
    const clustersForExport = categoryFilteredClusters;

    const buildClusterData = (clusterList) => {
      let data = clusterList.flatMap(cluster => [
        createMainKeywordExportRow(cluster),
        ...(cluster.keywords || []).map(kw => createSecondaryKeywordExportRow(cluster, kw, repeatCluster))
      ]);
      if (!includeZeroVolume) {
        data = data.filter(row => row['Volume'] > 0);
      }
      return data;
    };
    
    const buildExcludedData = () => {
      let data = excludedKeywords.map(kw => ({
        'Mot-clé': kw.keyword,
        'Volume': kw.volume || 0,
        'Position': kw.position || '-',
        'Trafic estimé': kw.estimatedTraffic || 0,
        'URL': kw.url || '-'
      }));
      if (!includeZeroVolume) {
        data = data.filter(row => row['Volume'] > 0);
      }
      return data;
    };
    
    if (separateExcluded && excludedKeywords.length > 0) {
      const clusterData = buildClusterData(clustersForExport);
      const excludedData = buildExcludedData();
      const sheets = [];
      if (clusterData.length > 0) {
        sheets.push({ name: 'Clusters', data: clusterData });
      }
      if (excludedData.length > 0) {
        sheets.push({ name: 'Non clusterises', data: excludedData });
      }
      if (sheets.length > 0) {
        exportToExcel(sheets, `optimization_${studyId}_${dateStr}${suffix}`, CLUSTER_EXPORT_COLUMNS);
      }
    } else {
      const dataToExport = buildClusterData(clustersForExport);
      exportToCSV(dataToExport, `optimization_${studyId}_${dateStr}${suffix}`, CLUSTER_EXPORT_COLUMNS);
    }
  };

  const totalPages = Math.ceil(filteredClusters.length / CLUSTERS_PER_PAGE);
  const startIndex = (currentPage - 1) * CLUSTERS_PER_PAGE;
  const paginatedClusters = filteredClusters.slice(startIndex, startIndex + CLUSTERS_PER_PAGE);

  if (loading) {
    return (
      <div className="optimization-container">
        <div className="loading-state">
          <Icon name="Loader" className="spinning" size={32} />
          <p>Chargement des clusters...</p>
        </div>
      </div>
    );
  }

  if (error && clusters.length === 0) {
    return (
      <div className="optimization-container">
        <div className="error-state">
          <Icon name="AlertCircle" size={32} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="optimization-container">
      <div className="optimization-header">
        <div className="header-left">
          <h2>Optimisation SEO</h2>
          {clusters.length > 0 && (
            <span className="clusters-count">
              {filteredClusters.length !== clusters.length 
                ? `${filteredClusters.length} / ${clusters.length} clusters`
                : `${clusters.length} cluster${clusters.length > 1 ? 's' : ''}`}
            </span>
          )}
        </div>
        <div className="header-right">
          <div className="search-wrapper">
            <Icon name="Search" size={16} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher un cluster..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <Icon name="X" size={14} />
              </button>
            )}
          </div>
          <div className="export-dropdown-container" ref={exportDropdownRef}>
            <button 
              className="export-btn"
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              disabled={clusters.length === 0}
            >
              <Icon name="Download" size={16} />
              Exporter
              <Icon name="ChevronDown" size={14} />
            </button>
            {exportDropdownOpen && (
              <div className="export-dropdown-menu">
                <div className="export-dropdown-header">Options d'export</div>
                <div className="export-dropdown-checkbox">
                  <input
                    type="checkbox"
                    id="repeat-cluster-name"
                    checked={repeatClusterName}
                    onChange={e => setRepeatClusterName(e.target.checked)}
                  />
                  <label htmlFor="repeat-cluster-name">Répéter le nom du cluster sur chaque ligne</label>
                </div>
                <button 
                  className="export-dropdown-option"
                  onClick={() => handleExport(true, false, repeatClusterName)}
                >
                  <Icon name="FileText" size={14} />
                  <div className="export-option-text">
                    <span>Tous les mots-clés</span>
                    <small>Inclut les volumes à 0</small>
                  </div>
                </button>
                <button 
                  className="export-dropdown-option"
                  onClick={() => handleExport(false, false, repeatClusterName)}
                >
                  <Icon name="FileText" size={14} />
                  <div className="export-option-text">
                    <span>Sans volume 0</span>
                    <small>Exclut les mots-clés sans volume</small>
                  </div>
                </button>
                {excludedKeywords.length > 0 && (
                  <>
                    <div className="export-dropdown-divider"></div>
                    <button 
                      className="export-dropdown-option"
                      onClick={() => handleExport(true, true, repeatClusterName)}
                    >
                      <Icon name="Files" size={14} />
                      <div className="export-option-text">
                        <span>+ Non clusterisés séparés</span>
                        <small>{excludedKeywords.length} mot{excludedKeywords.length > 1 ? 's' : ''}-clé{excludedKeywords.length > 1 ? 's' : ''} exclu{excludedKeywords.length > 1 ? 's' : ''}</small>
                      </div>
                    </button>
                    <button 
                      className="export-dropdown-option"
                      onClick={() => handleExport(false, true, repeatClusterName)}
                    >
                      <Icon name="Files" size={14} />
                      <div className="export-option-text">
                        <span>+ Non clusterisés (sans vol. 0)</span>
                        <small>Exclut les volumes à 0</small>
                      </div>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {clusters.length === 0 ? (
        <div className="empty-state">
          <Icon name="Package" size={48} />
          <p>Aucun cluster trouvé</p>
          <small>Créez des clusters dans l'onglet Clusterisation</small>
        </div>
      ) : filteredClusters.length === 0 ? (
        <div className="empty-state">
          <Icon name="Search" size={48} />
          <p>Aucun résultat pour "{searchTerm}"</p>
          <small>Essayez un autre terme de recherche</small>
        </div>
      ) : (
        <>
          <div className="clusters-list">
            {paginatedClusters.map(cluster => (
              <ClusterCard
                key={cluster.id}
                cluster={cluster}
                isViewed={!!viewedClusters[cluster.id]}
                onToggleViewed={toggleViewed}
                onUseUrl={handleUseUrl}
                onTargetUrlChange={handleTargetUrlChange}
              />
            ))}
          </div>
          
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(1)} 
                disabled={currentPage === 1}
              >
                «
              </button>
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <span className="pagination-info">
                Page {currentPage} / {totalPages}
              </span>
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                disabled={currentPage === totalPages}
              >
                ›
              </button>
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(totalPages)} 
                disabled={currentPage === totalPages}
              >
                »
              </button>
            </div>
          )}
          
          {excludedKeywords.length > 0 && (
            <div className="excluded-keywords-section">
              <div className="excluded-keywords-header">
                <h3>
                  <Icon name="EyeOff" size={18} />
                  Mots-clés non clusterisés ({excludedKeywords.length})
                </h3>
              </div>
              <div className="excluded-keywords-table-wrapper">
                <table className="excluded-keywords-table">
                  <thead>
                    <tr>
                      <th>Mot-clé</th>
                      <th>Volume</th>
                      <th>Position</th>
                      <th>Trafic estimé</th>
                      <th>URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excludedKeywords.map((kw, idx) => (
                      <tr key={`excluded-${idx}`}>
                        <td>{kw.keyword}</td>
                        <td>{formatNumber(kw.volume)}</td>
                        <td>
                          {kw.position ? (
                            <span className={`position-badge position-${kw.position <= 3 ? 'top' : kw.position <= 10 ? 'good' : 'low'}`}>
                              #{kw.position}
                            </span>
                          ) : (
                            <span className="no-position">-</span>
                          )}
                        </td>
                        <td>{formatNumber(kw.estimatedTraffic)}</td>
                        <td className="url-cell" title={kw.url}>
                          {kw.url ? (
                            <a href={kw.url} target="_blank" rel="noopener noreferrer">
                              {(() => {
                                const cleaned = cleanUrl(kw.url);
                                return cleaned.length > 50 ? cleaned.substring(0, 50) + '...' : cleaned;
                              })()}
                            </a>
                          ) : (
                            <span className="no-url">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OptimizationView;
